import { ArrowRight, FileWarning, RefreshCw, Send, ShieldAlert, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '@/api';
import type { VnnicCorrectionReport, VnnicCorrectionSync } from '@/api/areas/market';
import { Badge } from '@/components/Badge';
import { DataTable, Section, StatCard, StatGrid, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { EntityRef } from '@/components/EntityRef';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { Button } from '@/components/ui/button';
import { formatCount, formatDate, formatRelative } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { ActionDialog, EnumBadge, SuccessNote, useEnumLabel, When } from '../../compliance/ui';
import { ResubmitForm } from '../../compliance/vnnic/DossierSyncPage';
import { REPORT_STATUS, SYNC_STATUS, SyncErrorCode, TX_STATUS } from '../../compliance/vnnic/shared';
import { DOSSIER } from '../../domains/status';

/**
 * `/compliance/vnnic/corrections`: what VNNIC turned down and its correction trail. There is
 * no separate correction record: a dossier correction is a resubmission of its sync row, and a
 * report correction is a new revision of the report batch (spec §48, §51).
 */
export function VnnicCorrectionsPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  useDocumentTitle(t('vnnic.corrections.title'));
  const result = useApi((signal) => api.getVnnicCorrections({ signal }), []);
  const [resubmitting, setResubmitting] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const row = result.status === 'success' ? result.data.sync.find((r) => r.dossier_id === resubmitting) : undefined;
  const sync = result.status === 'success' ? result.data.sync : [];
  const reports = result.status === 'success' ? result.data.reports : [];

  const syncCols: Column<VnnicCorrectionSync>[] = [
    {
      key: 'domain',
      header: t('vnnic.corrections.col.domain'),
      cell: (r) => <EntityRef label={r.domain} to={`/domains/${encodeURIComponent(r.domain)}`} id={r.domain_id} sub={label('syncOperation', r.operation)} />,
    },
    {
      key: 'sync',
      header: t('vnnic.corrections.col.sync'),
      cell: (r) => <EnumBadge group="syncStatus" value={r.status} tones={SYNC_STATUS} />,
    },
    {
      key: 'dossier',
      header: t('vnnic.corrections.col.dossier'),
      cell: (r) => (
        <span className="flex items-center gap-1.5 text-sm">
          v{r.dossier_version} <EnumBadge group="dossierStatus" value={r.dossier_status} tones={DOSSIER} />
        </span>
      ),
    },
    {
      key: 'answer',
      header: t('vnnic.corrections.col.answer'),
      cell: (r) => (
        <div className="space-y-1 text-sm">
          {r.last_transaction ? (
            <>
              <div className="flex flex-wrap items-center gap-1.5">
                <EnumBadge group="txStatus" value={r.last_transaction.status} tones={TX_STATUS} />
                <span className="text-xs text-muted-foreground">
                  {t('vnnic.corrections.attemptVia', {
                    attempt: r.last_transaction.attempt,
                    adapter: r.last_transaction.adapter,
                  })}
                </span>
              </div>
              {r.last_transaction.error_code ? (
                <div className="text-xs">
                  <SyncErrorCode code={r.last_transaction.error_code} /> {r.last_transaction.error_message}
                </div>
              ) : null}
            </>
          ) : r.last_error_code ? (
            <SyncErrorCode code={r.last_error_code} />
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
      ),
    },
    {
      key: 'attempts',
      header: t('vnnic.corrections.col.attempts'),
      numeric: true,
      cell: (r) => r.attempts,
    },
    {
      key: 'since',
      header: t('vnnic.corrections.col.since'),
      cell: (r) => <When at={r.status_changed_at} />,
    },
    {
      key: 'action',
      header: t('vnnic.corrections.col.action'),
      cell: (r) => (
        <div className="flex flex-wrap items-center gap-2">
          {r.status === 'REJECTED' || r.status === 'CORRECTION_REQUIRED' ? (
            <Button
              size="sm"
              onClick={() => {
                setNotice(null);
                setResubmitting(r.dossier_id);
              }}
            >
              <Send /> {t('vnnic.sync.resubmit.open')}
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">
              {t('vnnic.corrections.nextAttempt', {
                when: formatRelative(r.next_attempt_at),
              })}
            </span>
          )}
          <Button asChild size="sm" variant="ghost">
            <Link
              to={
                r.last_transaction
                  ? `/compliance/vnnic/sync-transactions/${encodeURIComponent(r.last_transaction.id)}`
                  : `/compliance/vnnic/dossier-sync?dossier=${encodeURIComponent(r.dossier_id)}`
              }
            >
              {t('common.details')}
            </Link>
          </Button>
        </div>
      ),
    },
  ];

  const reportCols: Column<VnnicCorrectionReport>[] = [
    {
      key: 'report',
      header: t('vnnic.corrections.rcol.report'),
      cell: (b) => (
        <EntityRef
          label={b.report_type}
          to={`/compliance/vnnic/international-reports?batch=${encodeURIComponent(b.id)}`}
          id={b.id}
          sub={t('vnnic.corrections.schema', { version: b.schema_version })}
        />
      ),
    },
    {
      key: 'period',
      header: t('vnnic.corrections.rcol.period'),
      cell: (b) => (
        <span className="text-sm whitespace-nowrap">
          {formatDate(b.period_start)} → {formatDate(b.period_end)}
        </span>
      ),
    },
    {
      key: 'rev',
      header: t('vnnic.corrections.rcol.revision'),
      numeric: true,
      cell: (b) => b.revision,
    },
    {
      key: 'status',
      header: t('vnnic.corrections.rcol.status'),
      cell: (b) => (
        <>
          <EnumBadge group="reportStatus" value={b.status} tones={REPORT_STATUS} />
          {b.external_reference ? <div className="mt-1 font-mono text-xs">{b.external_reference}</div> : null}
        </>
      ),
    },
    {
      key: 'records',
      header: t('vnnic.corrections.rcol.records'),
      numeric: true,
      cell: (b) => b.record_count,
    },
    {
      key: 'invalid',
      header: t('vnnic.corrections.rcol.invalid'),
      numeric: true,
      cell: (b) => (
        <span className="inline-flex items-center gap-1">
          {b.invalid_count} / {b.rejected_items > 0 ? <Badge tone="crit">{b.rejected_items}</Badge> : 0}
        </span>
      ),
    },
    {
      key: 'chain',
      header: t('vnnic.corrections.rcol.chain'),
      cell: (b) => (
        <div className="space-y-1 text-sm">
          {b.supersedes_batch_id ? (
            <EntityRef
              label={t('vnnic.corrections.supersedes')}
              to={`/compliance/vnnic/international-reports?batch=${encodeURIComponent(b.supersedes_batch_id)}`}
              id={b.supersedes_batch_id}
            />
          ) : null}
          {b.superseded_by_batch_id ? (
            <EntityRef
              label={t('vnnic.corrections.supersededBy')}
              to={`/compliance/vnnic/international-reports?batch=${encodeURIComponent(b.superseded_by_batch_id)}`}
              id={b.superseded_by_batch_id}
            />
          ) : b.status === 'REJECTED' ? (
            <Badge tone="warn">{t('vnnic.corrections.needsRevision')}</Badge>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('vnnic.corrections.title')}
        crumbs={[{ label: t('crumbs.compliance') }, { label: t('crumbs.vnnic') }]}
        subtitle={t('vnnic.corrections.subtitle')}
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-6">
        {notice ? <SuccessNote>{notice}</SuccessNote> : null}
        {result.status === 'success' ? (
          <StatGrid className="xl:grid-cols-3">
            <StatCard
              label={t('vnnic.corrections.stats.rejected')}
              value={formatCount(sync.filter((r) => r.status === 'REJECTED').length)}
              tone={sync.some((r) => r.status === 'REJECTED') ? 'crit' : 'ok'}
              icon={ShieldAlert}
            />
            <StatCard
              label={t('vnnic.corrections.stats.correction')}
              value={formatCount(sync.filter((r) => r.status === 'CORRECTION_REQUIRED').length)}
              tone={sync.some((r) => r.status === 'CORRECTION_REQUIRED') ? 'serious' : 'ok'}
              icon={Undo2}
            />
            <StatCard
              label={t('vnnic.corrections.stats.reports')}
              value={formatCount(reports.filter((b) => b.status === 'REJECTED' && !b.superseded_by_batch_id).length)}
              tone={reports.some((b) => b.status === 'REJECTED' && !b.superseded_by_batch_id) ? 'warn' : 'ok'}
              icon={FileWarning}
            />
          </StatGrid>
        ) : null}
        <Section
          title={t('vnnic.corrections.sync')}
          actions={
            <Button asChild variant="ghost" size="sm">
              <Link to="/compliance/vnnic/dossier-sync">
                {t('vnnic.corrections.allSyncs')} <ArrowRight />
              </Link>
            </Button>
          }
          flush
        >
          {result.status === 'loading' ? (
            <div className="px-5">
              <Loading rows={3} />
            </div>
          ) : null}
          {result.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={result.error} onRetry={result.reload} title={t('vnnic.corrections.loadError')} />
            </div>
          ) : null}
          {result.status === 'success' ? (
            <DataTable columns={syncCols} rows={sync} rowKey={(r) => r.dossier_id} empty={<EmptyState title={t('vnnic.corrections.nothing')} />} />
          ) : null}
        </Section>
        <Section title={t('vnnic.corrections.reports')} description={t('vnnic.corrections.reportsHint')} flush>
          {result.status === 'success' ? (
            <DataTable columns={reportCols} rows={reports} rowKey={(b) => b.id} empty={<EmptyState title={t('vnnic.corrections.noReports')} />} />
          ) : null}
          {result.status === 'loading' ? (
            <div className="px-5">
              <Loading rows={2} />
            </div>
          ) : null}
        </Section>
      </div>
      <ActionDialog open={Boolean(row)} onOpenChange={(o) => !o && setResubmitting(null)} title={row ? t('vnnic.sync.resubmit.title', { domain: row.domain }) : ''}>
        {row ? (
          <ResubmitForm
            dossierId={row.dossier_id}
            version={row.dossier_version}
            onCancel={() => setResubmitting(null)}
            onDone={() => {
              setNotice(t('vnnic.sync.resubmit.doneDomain', { domain: row.domain }));
              setResubmitting(null);
              result.reload();
            }}
          />
        ) : null}
      </ActionDialog>
    </>
  );
}
